import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Paper,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Header from "./MenuBar";
import Footer from "./Footer";
const envVariable = require("../EnvironmentVariables.json");

const InvoiceHistory = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasPaidInvoice, setHasPaidInvoice] = useState(false);
  const [activeInvoiceId, setActiveInvoiceId] = useState(null);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  const { taskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const customerName = location.state?.customerName ?? "Customer";
  const customerId = location.state?.customerId ?? null;
  const vin_number = location.state?.vin_number ?? null;
  const originDate = location.state?.originDate ?? null;
  const time = location.state?.time ?? null;

  // Parse datetime string from backend as UTC (backend stores all timestamps in UTC)
  const parseUTCDateTime = (dateString) => {
    if (!dateString) return null;
    // Backend returns format: "YYYY-MM-DD HH:MM:SS"
    // Replace space with T and add Z to indicate UTC
    const isoString = dateString.replace(' ', 'T') + (dateString.includes('Z') ? '' : 'Z');
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? null : date;
  };

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const response = await fetch(
          `${envVariable.serverId}/Payment/getInvoiceHistory.php?task_id=${taskId}`
        );
        const data = await response.json();
        if (response.ok && data.status === "success") {
          const normalized = Array.isArray(data.invoices)
            ? data.invoices.map((invoice) => ({
                ...invoice,
                invoice_id: Number(invoice.invoice_id),
                version: Number(invoice.version),
                is_active: Boolean(invoice.is_active),
              }))
            : [];
          setInvoices(normalized);
          setHasPaidInvoice(Boolean(data.has_paid_invoice));
          setActiveInvoiceId(
            data.active_invoice_id !== null && data.active_invoice_id !== undefined
              ? Number(data.active_invoice_id)
              : null
          );
        } else {
          throw new Error(data.message || "Failed to fetch invoices");
        }
      } catch (error) {
        console.error("Error fetching invoices:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [taskId]);

  const handleCreateNewInvoice = () => {
    if (hasPaidInvoice) {
      return;
    }

    navigate(`/tasks/generate-invoice/${taskId}`, {
      state: {
        customerId: customerId,
        customerName: customerName,
        originDate: originDate,
        vin_number: vin_number,
        time: time,
        numInvoices: invoices.length,
        carId: location.state.carId,
      },
    });
  };

  const handleCancelClick = (invoice_id) => {
    setSelectedInvoiceId(invoice_id);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    setCancelDialogOpen(false);
    if (!selectedInvoiceId) return;

    try {
      const res = await fetch(envVariable.serverId + "/Payment/cancelInvoice.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invoice_id: selectedInvoiceId }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setInvoices((prev) =>
          prev.map((inv) =>
            inv.invoice_id === selectedInvoiceId
              ? { ...inv, status: "Canceled", is_active: false }
              : inv
          )
        );
        if (activeInvoiceId === selectedInvoiceId) {
          setActiveInvoiceId(null);
        }
      } else {
        console.error("Failed to cancel invoice:", data.message);
      }
    } catch (error) {
      console.error("Error calling cancelInvoice:", error);
    }
  };

  const handleBackToTasks = () => {
    navigate("/tasks");
  };

  return (
    <>
      <Header />
      <Container maxWidth="md">
        <Box mt={5} mb={3}>
          <Box display="flex" alignItems="center" mb={2}>
            <IconButton
              onClick={handleBackToTasks}
              sx={{ mr: 2 }}
              aria-label="back to tasks"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              Invoice History
            </Typography>
          </Box>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" mt={5}>
              <CircularProgress />
              <Typography variant="body1" ml={2}>
                Loading invoices...
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={3}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Version</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Status</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Created At</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No invoices found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.map((invoice) => {
                      const isActiveRow = activeInvoiceId === invoice.invoice_id;
                      const isReplaced = invoice.status === "Replaced";
                      const chipColor =
                        invoice.status === "Paid"
                          ? "success"
                          : invoice.status === "Canceled"
                          ? "error"
                          : invoice.status === "Replaced"
                          ? "default"
                          : invoice.status === "Refunded"
                          ? "info"
                          : invoice.status === "Paid, Refund Rejected"
                          ? "error"
                          : "warning";
                      // Parse UTC created_at and display in user's local timezone
                      const createdAtDate = parseUTCDateTime(invoice.created_at);
                      const createdAt = createdAtDate
                        ? createdAtDate.toLocaleString()
                        : "—";
                      const canCancel = isActiveRow && invoice.status === "Unpaid";
                      const parsedTotal = Number(invoice.total);
                      const totalValue = Number.isFinite(parsedTotal) ? parsedTotal : 0;

                      return (
                        <TableRow
                          key={invoice.invoice_id}
                          sx={{
                            backgroundColor: isActiveRow
                              ? "#e8f5e9"
                              : isReplaced
                              ? "#f5f5f5"
                              : "inherit",
                            opacity: !isActiveRow && isReplaced ? 0.75 : 1,
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              v{invoice.version}
                              {isActiveRow && (
                                <Chip label="ACTIVE" color="success" size="small" />
                              )}
                              {isReplaced && !isActiveRow && (
                                <Chip label="REPLACED" size="small" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={invoice.status} color={chipColor} size="small" />
                          </TableCell>
                          <TableCell>${totalValue.toFixed(2)}</TableCell>
                          <TableCell>{createdAt}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={!invoice.content}
                              onClick={() =>
                                invoice.content &&
                                window.open(invoice.content, "_blank", "noopener,noreferrer")
                              }
                              sx={{ mr: canCancel ? 1 : 0, mb: { xs: 1, sm: 0 } }}
                            >
                              View PDF
                            </Button>
                            {canCancel && (
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => handleCancelClick(invoice.invoice_id)}
                              >
                                Cancel Payment
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box mt={4} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateNewInvoice}
              disabled={hasPaidInvoice}
            >
              {hasPaidInvoice ? "Task Paid" : "Create New Invoice"}
            </Button>
            {hasPaidInvoice && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2, alignSelf: "center" }}>
                Cannot create new invoices for paid tasks
              </Typography>
            )}
          </Box>
        </Box>
      </Container>

      {/* confirmation */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Confirm Cancel Payment</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to cancel the payment for invoice #{selectedInvoiceId}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>No</Button>
          <Button color="error" onClick={handleConfirmCancel}>
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </>
  );
};

export default InvoiceHistory;
